import { Title, Button } from "rizzui";
import { IEnquiryEntry } from "@/utilities/types/EnquiryEntry";
import { useState, useEffect } from "react";
import QuillEditor from "@/components/ui/quille-editor/quill-editor";
import EnquiryEntriesRepository from "@/utilities/repositories/EnquiryEntries";
import useApi from "@/hooks/useApi";

interface NotesSectionProps {
  data?: IEnquiryEntry;
  isAdmin?: boolean;
  onRefresh?: () => void;
  isReadOnly?: boolean;
}

export default function NotesSection({
  data,
  isAdmin = false,
  onRefresh,
  isReadOnly = false,
}: NotesSectionProps) {
  const [userNote, setUserNote] = useState<string | null>(null);
  const [adminNote, setAdminNote] = useState<string | null>(null);
  const { request: updateEnquiryEntry, loading } = useApi({
    request: EnquiryEntriesRepository.update,
    enableToast: true,
  });

  // Check if data is EnquiryEntry type
  const isEnquiryEntry = (obj?: any): obj is IEnquiryEntry => {
    return obj && "note" in obj;
  };

  const enquiryEntryData = isEnquiryEntry(data)
    ? (data as IEnquiryEntry)
    : undefined;

  useEffect(() => {
    if (!data?.id || !isEnquiryEntry(data)) return;

    setUserNote(data.note || "");
    setAdminNote(data.adminNote || "");
  }, [data?.id, data]);

  const hasChanges =
    enquiryEntryData &&
    (enquiryEntryData.note !== userNote ||
      enquiryEntryData.adminNote !== adminNote);

  if (!isEnquiryEntry(data)) {
    return null;
  }

  return (
    <div className="space-y-6  p-3 bg-white">
      {/* User Notes Section */}
      <div className="space-y-4">
        <div>
          <Title as="h3" className="text-xl font-semibold text-gray-900 mb-2">
            User Notes
          </Title>
          <p className="text-sm text-gray-500 mb-4">Key in your notes here.</p>
        </div>

        <QuillEditor
          value={userNote}
          readOnly={isReadOnly}
          onChange={(value) => setUserNote(value)}
          className="[&_.ql-editor]:min-h-[120px] [&_.ql-editor]:max-h-[180px] text-lg"
          modules={{
            toolbar: [
              [{ header: [1, 2, 3, false] }],
              ["bold", "italic", "underline", "strike"],
              [{ color: [] }, { background: [] }],
              [{ list: "ordered" }, { list: "bullet" }],
              ["link", "clean"],
            ],
          }}
        />
      </div>
      {/* ProjectSG Notes Section */}
      <div className="space-y-4">
        <div>
          <Title as="h3" className="text-xl font-semibold text-gray-900 mb-2">
            ProjectSG Notes
          </Title>
          <p className="text-sm text-gray-500 mb-4">
            Editor access is restricted to ProjectSG.
          </p>
        </div>

        <QuillEditor
          value={adminNote}
          onChange={(value) => setAdminNote(value)}
          className="[&_.ql-editor]:min-h-[120px] [&_.ql-editor]:max-h-[180px] text-lg"
          readOnly={!isAdmin}
          modules={{
            toolbar: isAdmin
              ? [
                  [{ header: [1, 2, 3, false] }],
                  ["bold", "italic", "underline", "strike"],
                  [{ color: [] }, { background: [] }],
                  [{ list: "ordered" }, { list: "bullet" }],
                  ["link", "clean"],
                ]
              : false,
          }}
        />
      </div>

      {/* Save Button */}
      {!isReadOnly && (
        <div className="flex justify-end pt-2">
          <Button
            onClick={async () => {
              if (!data?.id) return;
              await updateEnquiryEntry(data.id, {
                note: userNote,
                adminNote: adminNote,
              });

              onRefresh && onRefresh();
            }}
            disabled={!hasChanges}
            isLoading={loading}
            className="px-6 py-2"
          >
            {loading ? "Saving..." : "Save Notes"}
          </Button>
        </div>
      )}
    </div>
  );
}
